import { buildApiFilterParams } from "@/utils/apiFilterParams";

export const HOME_KPI_STATS_URL = {
    sales: "/api/customerorders/stats/",
    sales_by_item: "/api/customerorderitems/stats/",
    stock: "/api/stockproducts/stats/",
    stock_value: "/api/products/stats/",
    shipments: "/api/shipments/stats/",
    receipts: "/api/receipts/stats/",
    purchases: "/api/providerorders/stats/",
    customer_invoices: "/api/customerinvoices/stats/",
    provider_invoices: "/api/providerinvoices/stats/",
};

export const HOME_KPI_TYPE_PERMISSION = {
    sales: "order.view_customerorder",
    sales_by_item: "order.view_customerorder",
    stock: "core.view_product",
    stock_value: "core.view_product",
    shipments: "core.view_shipment",
    receipts: "core.view_receipt",
    purchases: "order.view_providerorder",
    customer_invoices: "billing.view_customerinvoice",
    provider_invoices: "billing.view_providerinvoice",
};

/** Scope codes aligned with navigation (not incidental permissions). */
export const HOME_KPI_TYPE_SCOPES = {
    sales: ["read_customerorders", "write_customerorders", "assign_customerorders"],
    sales_by_item: ["read_customerorders", "write_customerorders", "assign_customerorders"],
    stock: ["read_stocks", "write_stocks"],
    stock_value: ["read_products", "write_products", "read_stocks", "write_stocks"],
    shipments: ["read_shipments", "write_shipments"],
    receipts: ["read_receipts", "write_receipts"],
    purchases: ["read_providerorders", "write_providerorders"],
    customer_invoices: ["read_customerinvoices", "write_customerinvoices"],
    provider_invoices: ["read_providerinvoices", "write_providerinvoices"],
};

export const HOME_KPI_ICON = {
    sales: "orders",
    sales_by_item: "product",
    stock: "stock",
    stock_value: "euro",
    shipments: "shipment",
    receipts: "receipt",
    purchases: "orders",
    customer_invoices: "invoice",
    provider_invoices: "invoice",
};

export const HOME_KPI_METRIC_FORMAT = {
    count: "integer",
    orders: "integer",
    total: "integer",
    total_items: "integer",
    orders_without_margin: "integer",
    orders_unassigned: "integer",
    total_quantity: "integer",
    warehouses_count: "integer",
    stock_count: "integer",
    products: "integer",
    shipments: "integer",
    receipts: "integer",
    invoices: "integer",
    items_count: "integer",
    items_total: "integer",
    pending_buyer_validation_count: "integer",
    average: "decimal",
    amount_excl_vat_avg: "decimal",
    total_amount_excl_vat_sum: "euros",
    total_amount_incl_vat_sum: "euros",
    total_revenue_sum: "euros",
    total_margin_excl_vat_sum: "euros",
    total_agent_commission_sum: "euros",
    average_basket_excl_vat: "euros",
    amount_excl_vat_sum: "euros",
    amount_incl_vat_sum: "euros",
    stock_value: "euros",
    total_purchase_price_excl_vat: "euros",
    total_amount_excl_vat: "euros",
    total_amount_incl_vat: "euros",
    revenue_ratio: "percent",
    total_margin_ratio: "percent",
    total_agent_commission_ratio: "percent",
};

/** VAT basis for euro KPI metrics (null = no suffix). */
export const HOME_KPI_METRIC_AMOUNT_BASIS = {
    total_amount_excl_vat_sum: "excl_vat",
    total_amount_incl_vat_sum: "incl_vat",
    total_revenue_sum: "excl_vat",
    total_margin_excl_vat_sum: "excl_vat",
    total_agent_commission_sum: "excl_vat",
    average_basket_excl_vat: "excl_vat",
    amount_excl_vat_sum: "excl_vat",
    amount_incl_vat_sum: "incl_vat",
    amount_excl_vat_avg: "excl_vat",
    stock_value: "excl_vat",
    total_purchase_price_excl_vat: "excl_vat",
    total_amount_excl_vat: "excl_vat",
    total_amount_incl_vat: "incl_vat",
};

export function resolveMetricAmountBasis(metricKey) {
    if (!metricKey) {
        return null;
    }
    if (HOME_KPI_METRIC_AMOUNT_BASIS[metricKey]) {
        return HOME_KPI_METRIC_AMOUNT_BASIS[metricKey];
    }
    if (String(metricKey).includes("incl_vat")) {
        return "incl_vat";
    }
    if (String(metricKey).includes("excl_vat")) {
        return "excl_vat";
    }
    return null;
}

export function formatMetricAmountBasisSuffix(metricKey, translate) {
    const basis = resolveMetricAmountBasis(metricKey);
    if (basis === "incl_vat") {
        return translate("catalogentry.labels.inclVat");
    }
    if (basis === "excl_vat") {
        return translate("catalogentry.labels.exclVat");
    }
    return null;
}

export const HOME_KPI_AUTO_SUBTITLE = {
    sales: {
        total_amount_excl_vat_sum: "revenue_ratio",
        total_revenue_sum: "revenue_ratio",
        total_margin_excl_vat_sum: "total_margin_ratio",
        total_agent_commission_sum: "total_agent_commission_ratio",
    },
    sales_by_item: {},
};

export function buildStatsParams(filters = {}) {
    return buildApiFilterParams(filters);
}

export function extractMetricValue(data, metricKey) {
    if (!data || metricKey == null) {
        return null;
    }
    const value = data[metricKey];
    if (value === undefined || value === null) {
        return null;
    }
    return value;
}

export function resolveSubtitleMetricKey(definition, statsData) {
    if (definition.subtitle_metric_key) {
        return definition.subtitle_metric_key;
    }
    const auto = HOME_KPI_AUTO_SUBTITLE[definition.kpi_type] || {};
    return auto[definition.metric_key] || null;
}

const METRIC_TO_STAT_FORMAT = {
    integer: "number",
    decimal: "number",
    number: "number",
    euros: "euros",
    percent: "percent",
    string: "string",
};

export function formatForMetric(metricKey, definition) {
    let raw;
    if (definition?.metric_format) {
        raw = definition.metric_format;
    } else {
        raw = HOME_KPI_METRIC_FORMAT[metricKey] || "decimal";
    }
    return METRIC_TO_STAT_FORMAT[raw] || "number";
}

export function canViewKpiType(type, scopes, { isCustomer, isOwner = false, isOperator = false } = {}) {
    if (type === "purchases" && isOperator) {
        return false;
    }
    if (type === "stock_value" && !isCustomer) {
        return false;
    }
    if (isOwner) {
        return true;
    }
    const requiredScopes = HOME_KPI_TYPE_SCOPES[type];
    if (!requiredScopes?.length) {
        return false;
    }
    const held = scopes || [];
    return requiredScopes.some((code) => held.includes(code));
}
